import { useScroll, useSpring, SpringValue } from 'react-spring'
import { useEffect, useRef, useState } from 'react'
import useBreakpoint from './useBreakPoint'

type UseHeaderScrollAnimateProps = {
  animation: { hide: string; show: string }
  hideNavOnScroll?: boolean
  hideNavForSupportUsers?: boolean
}

const useHeaderScrollAnimate = ({
  animation,
  hideNavOnScroll = true,
  hideNavForSupportUsers,
}: UseHeaderScrollAnimateProps): { transform: SpringValue<string> } => {
  const [hideNav, setHideNav] = useState<boolean>(false)

  const headerPositionStyle = useSpring({
    transform: hideNav ? animation.hide : animation.show,
    config: { tension: 250, friction: 30 },
  })

  const { isMobileScreen } = useBreakpoint()
  const previousYScroll = useRef(0)
  const previousIsMobileScreen = useRef(isMobileScreen)

  useEffect(() => {
    // Detect transition from mobile to desktop
    const switchedToDesktop = previousIsMobileScreen.current && !isMobileScreen
    previousIsMobileScreen.current = isMobileScreen

    if (switchedToDesktop && hideNav) {
      // Reset the nav to visible when switching from mobile to desktop
      // Added this set timeout because for some reason after switching from mobile to desktop, it doesn't seem to set the animation if it done immediately
      setTimeout(() => {
        setHideNav(false)
      }, 700)
    }

    if (hideNavOnScroll) {
      // Enable scroll animation for both mobile and desktop when hideNavOnScroll is true
      headerPositionStyle.transform.resume()
    } else if (!isMobileScreen && hideNav) {
      // Resets the nav hidden animation after switching to desktop from mobile, to show the nav on desktop and then disable the hide/show nav on scroll.
      // Added this set timeout because for some reason after switching from mobile to desktop, it doesn't seem to set the animation if it done immediately
      setTimeout(() => {
        setHideNav(false)
        setTimeout(() => {
          headerPositionStyle.transform.pause()
        }, 700)
      }, 700)
    } else if (hideNavForSupportUsers && hideNavOnScroll) {
      // For support users, hide the nav bar on desktop screens as well.
      headerPositionStyle.transform.resume()
    } else {
      // Pause animation when hideNavOnScroll is false
      headerPositionStyle.transform.pause()
    }
  }, [isMobileScreen, hideNavOnScroll, hideNav])

  useScroll({
    onChange: ({ value: { scrollYProgress, scrollY } }) => {
      if (previousYScroll.current - scrollYProgress > 0) {
        // When scrolling to the top
        setHideNav(false)
      } else if (
        previousYScroll.current - scrollYProgress < 0 &&
        // Reason for this condition is because we don't want to hide the nav till the scroll position exceeds the nav height
        // Or is closer to the nav height because the empty space on the first scroll looks ugly
        scrollY > 100
      ) {
        // When scrolling to the bottom
        setHideNav(true)
      }

      previousYScroll.current = scrollYProgress
    },
  })

  return headerPositionStyle
}

export default useHeaderScrollAnimate
